import { IPostFilters, IPaginatedResponse, IPost } from '@sdk/contracts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { create as createStore } from 'zustand';

import { sdk } from '@/services';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 9;

const PostService = sdk.getService('PostService');

interface IStore {
  filter: Partial<IPostFilters>;
  search: string;
  setFilter: (filter: Partial<IPostFilters>) => void;
  setSearch: (search: string) => void;
  resetFilters: () => void;
}

const store = createStore<IStore>(set => ({
  filter: {},
  search: '',
  setFilter: (filter: Partial<IPostFilters>) => {
    set({ filter });
  },
  setSearch: (search: string) => {
    set({ search });
  },
  resetFilters: () => set({ filter: {}, search: '' })
}));

interface UsePostsListOptions {
  filters?: IPostFilters;
}

export const usePostsList = (options: UsePostsListOptions = {}) => {
  const { filters: externalFilters } = options;
  const { filter, search, setFilter, setSearch, resetFilters } = store();

  const mergedFilters: IPostFilters = {
    ...externalFilters,
    ...filter
  };

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery<
    IPaginatedResponse<IPost>,
    Error
  >({
    queryKey: ['posts', { search, filters: mergedFilters }],
    initialPageParam: DEFAULT_PAGE,
    queryFn: ({ pageParam = DEFAULT_PAGE }) => {
      return PostService.getPosts(
        { page: pageParam as number, limit: DEFAULT_LIMIT },
        mergedFilters,
        search
      );
    },
    getNextPageParam: lastPage => (lastPage.hasNextPage ? lastPage.nextPage : undefined)
  });

  const loadMorePosts = useCallback(() => {
    if (isFetchingNextPage || !hasNextPage) return;
    fetchNextPage();
  }, [isFetchingNextPage, hasNextPage, fetchNextPage]);

  const flatPages = (data?.pages ?? []) as IPaginatedResponse<IPost>[];
  const posts = flatPages.flatMap(p => p.docs);

  const lastPage = flatPages[flatPages.length - 1];

  const pagination = lastPage && {
    page: lastPage.page,
    limit: lastPage.limit,
    hasNextPage: lastPage.hasNextPage,
    hasPrevPage: lastPage.hasPrevPage,
    nextPage: lastPage.nextPage,
    pagingCounter: lastPage.pagingCounter,
    prevPage: lastPage.prevPage,
    totalDocs: lastPage.totalDocs,
    totalPages: lastPage.totalPages
  };

  const filtersCount = Object.keys(filter).length + (search ? 1 : 0);

  return {
    isPending: isLoading || isFetchingNextPage,
    loadMorePosts,
    pagination,
    posts,
    filter,
    search,
    setFilter,
    setSearch,
    resetFilters,
    filtersCount,
    hasNextPage,
    isFetchingNextPage
  };
};
